import type { EstadoResuelto } from './tipos'

/**
 * El color lo decide el servidor (columna `color` de la tabla `estados`), aquí
 * solo se traduce a clases. Así un estado nuevo sale pintado sin tocar esto.
 */
const GRIS = 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'

const CLASES: Record<string, string> = {
  verde: 'bg-marca-100 text-marca-800 dark:bg-marca-700/30 dark:text-marca-100',
  naranja: 'bg-amber-100 text-amber-800',
  azul: 'bg-sky-100 text-sky-700',
  rojo: 'bg-red-100 text-red-700',
  morado: 'bg-purple-100 text-purple-700',
  gris: GRIS,
}

export const clasesDeColor = (color: string): string => CLASES[color] ?? GRIS

/**
 * Lo que se enseña del estado.
 *
 * «BAJA» y «REVISIÓN» a secas no se le enseñan a nadie: son cabeceras de grupo y
 * lo que vale es el subestado («baja descomisionable», «2.ª llamada»).
 */
export const etiquetaDeEstado = (estado: EstadoResuelto): string =>
  estado.subestado_etiqueta ?? estado.estado_etiqueta

/**
 * El estado del punto de suministro, como se le dice al comercial.
 *
 * Las mismas etiquetas que en Dominio\Suministro. Se repiten aquí para poder
 * pintar el estado que ya está guardado en la ficha sin volver a consultarlo.
 * «Sin comprobar» no es un fallo: es lo que hay mientras el CRM trabaje en modo
 * manual, y hay que distinguirlo de «lo hemos mirado y está activo».
 */
const SUMINISTRO: Record<string, string> = {
  activo: 'Activo',
  suspendido: 'Suspendido por impago',
  baja: 'De baja',
  desconocido: 'Sin comprobar',
}

export const etiquetaDeSuministro = (estado: string): string => SUMINISTRO[estado] ?? estado

/** Estados en los que la ficha todavía se está rellenando. */
export const ABIERTOS = ['BORRADOR', 'PENDIENTE']

export const estaAbierta = (estado: { estado_codigo: string }): boolean =>
  ABIERTOS.includes(estado.estado_codigo)

/**
 * Lo que queda del plazo del estado, en palabras.
 *
 * El servidor manda `vence_el` ya calculado en horas laborables y festivos de
 * Sevilla, así que aquí solo se resta. Viene en UTC sin zona, como lo da
 * MariaDB, de ahí la Z.
 */
export const cuentaAtras = (
  venceEl: string | null,
): { texto: string; apura: boolean } | null => {
  if (venceEl === null) return null

  const vence = new Date(venceEl.replace(' ', 'T') + 'Z').getTime()
  if (Number.isNaN(vence)) return null

  const horas = Math.floor((vence - Date.now()) / 3_600_000)

  if (horas < 0) return { texto: 'plazo cumplido', apura: true }
  if (horas < 24) return { texto: `quedan ${horas} h`, apura: true }

  return { texto: `quedan ${Math.floor(horas / 24)} d`, apura: false }
}
